import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration } from '../../data/configuration.js';
import { Utilities } from '../../utilities/utilities.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface CallSignsViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface CallSignsViewContextType {
	selectedFlagItemId: string;
	handleFlagItemClicked: (id:string) => void;
	customFlagValue: string;
	handleCustomFlagInputChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleFlagItemInputFocused: (id:string) => void;
}

interface PilotInputProps {
	type: 'pilot' | 'copilot';
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function CallSignsView(props: CallSignsViewProps) {

	// initial values.
	const initialPilotValue = Configuration.pilotValue;
	const initialCopilotValue = Configuration.copilotValue;
	const initialFlagId = Configuration.callSignsFlagOption;
	const initialCustomFlagValue = Configuration.customFlagValue;

	// states.
	const [pilotValue, setPilotValue] = useState(initialPilotValue);
	const [copilotValue, setCopilotValue] = useState(initialCopilotValue);
	const [selectedFlagItemId, setSelectedFlagItemId] = useState(initialFlagId);
	const [customFlagValue, setCustomFlagValue] = useState(initialCustomFlagValue);

	//handlers.

	const handlePilotValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		// update the pilot value.
		setPilotValue(value);
		props.exteriorConfigurator.selectCallSignsText('pilot', value);

		// update the configuration.
		Configuration.pilotValue = value;
	}

	const handleCopilotValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		// update the copilot value.
		setCopilotValue(value);
		props.exteriorConfigurator.selectCallSignsText('copilot', value);


		// update the configuration.
		Configuration.copilotValue = value;
	}

	const handleFlagItemClicked = (id: string) => {

		// toggle the item.
		if (id === selectedFlagItemId) {
			id = 'NONE';
		}

		// update the selected item.
		setSelectedFlagItemId(id);

		// update the configuration.
		Configuration.callSignsFlagOption = id;
		Configuration.updateCosts();

		props.exteriorConfigurator.selectCallSigns(id);
	}

	const handleFlagItemInputFocused = (id: string) => {
		
		// update the selected item.
		setSelectedFlagItemId(id);

		// update the configuration.
		Configuration.callSignsFlagOption = id;
		Configuration.updateCosts();
	}

	const handleCustomFlagInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		// update the custom flag value.
		setCustomFlagValue(value);

		// update the configuration.
		Configuration.customFlagValue = value;

	}

	// return the HTML.
	return (
		<CallSignsViewContext.Provider 
			value={{				 
				selectedFlagItemId, 
				handleFlagItemClicked, 
				customFlagValue,
				handleCustomFlagInputChanged,
				handleFlagItemInputFocused
			}}
		>
			<VehicleView onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-call-signs">
							CALL SIGNS
						</h2>
					</div>

					<SectionPanel
						subHeading={'FLAG'}
						modalContent={CloudData.getModalContent(selectedFlagItemId, CloudData.graphicsOptions)}
						sectionValue={
							<p className="section-value">
								{selectedFlagItemId !== 'NONE' && getItemName(selectedFlagItemId) + ' ' + Utilities.asCurrency(CloudData.getPrice(selectedFlagItemId, CloudData.graphicsOptions, { ref: false }), Configuration.regionCode)}
							</p>
						}
					>
						<FlagItems />
					</SectionPanel>
					{selectedFlagItemId !== 'NONE' && (
						<div>
							<SectionPanel
								subHeading={'PILOT'}
								sectionValue={
									<p className={`section-value${selectedFlagItemId === 'NONE' ? ' disabled' : ''}`}>
										{pilotValue}
									</p>
								}
								shouldStartClosed={true}
							>
								<PilotInput type={'pilot'} onChange={handlePilotValueChanged} value={pilotValue} />
							</SectionPanel>

							<SectionPanel
								subHeading={'COPILOT'}
								sectionValue={
									<p className={`section-value${selectedFlagItemId === 'NONE' ? ' disabled' : ''}`}>
										{copilotValue}
									</p>
								}
								shouldStartClosed={true}
							>
								<PilotInput type={'copilot'} onChange={handleCopilotValueChanged} value={copilotValue} />
							</SectionPanel>
						</div>
					)}
				</div>
			</VehicleView>
		</CallSignsViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const CallSignsViewContext = createContext<CallSignsViewContextType | undefined>(undefined);

function PilotInput(props: PilotInputProps) {

	// get the context and ensure it is valid.
	const context = useContext(CallSignsViewContext);
	if (!context) {
		throw new Error('CallSignsViewContext must be used within a CallSignsView component');
	}
	const isDisabled = context.selectedFlagItemId === 'NONE';
	const characterLimitReached = props.value.length >= 20;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value.length > 20) return;

		props.onChange(e);
	}

	// return the html.
	return (
		<React.Fragment>
			{characterLimitReached && <p className="pilot-input-disclaimer">* max 20 characters</p>}
			<div className="pilot-input-container">
				<input
					value={props.value}
					disabled={isDisabled}
					onChange={handleChange}
					placeholder={`${props.type.toUpperCase()} NAME`} />
			</div>
		</React.Fragment>

	);
}

function FlagItems() {
	return (
		<ViewContentRows>
			<FlagItem id={'DK011'} imageFilePath={'uk'} />
			<CustomFlagItem id={'DK012'} imageFilePath={'custom-flag'} />
		</ViewContentRows>
	);
};

function FlagItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(CallSignsViewContext);
	if (!context) {
		throw new Error('CallSignsViewContext must be used within a CallSignsView component');
	}

	// return the HTML.
	return (
		<div className="flag-item-container">
			<SectionItem
				id={props.id}
				itemName={getItemName(props.id)}
				imageFilePath={`assets/images/configuration-items/exterior/flags/${props.imageFilePath}.webp`}
				isSelected={props.id === context.selectedFlagItemId}
				onItemClicked={context.handleFlagItemClicked}
			/>
		</div>
	);
}

function CustomFlagItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(CallSignsViewContext);
	if (!context) {
		throw new Error('CallSignsViewContext must be used within a CallSignsView component');
	}

	// return the HTML.
	return (
		<div className="flag-item-container">
			<SectionItem
				id={props.id}
				itemName={getItemName(props.id)}
				imageFilePath={`assets/images/configuration-items/exterior/flags/${props.imageFilePath}.webp`}
				isSelected={props.id === context.selectedFlagItemId}
				onItemClicked={context.handleFlagItemClicked}
			/>
		</div>
	);
}


//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.

function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.graphicsOptions);
}